"use client";

import React, { useEffect, useRef, useState } from "react";
import { Clock, Monitor, Tag, CheckCircle2, BookOpen, Award, Zap, Shield } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const stats = [{
  id: "duration",
  label: "Duración",
  value: "6 Meses",
  icon: Clock,
  valueColor: "text-white"
}, {
  id: "modality",
  label: "Modalidad",
  value: "100% Presencial",
  icon: Monitor,
  valueColor: "text-white"
}, {
  id: "price",
  label: "Inversión",
  value: "$U 20.000",
  suffix: "/mes",
  icon: Tag,
  valueColor: "text-secondary"
}];
const modules = [{
  id: "mod-1",
  title: "Módulo 1: Introducción a la Gestión Forestal",
  content: "Estudio de los ecosistemas forestales, identificación de especies nativas y exóticas, y principios básicos de la silvicultura sostenible en el contexto regional.",
  icon: BookOpen
}, {
  id: "mod-2",
  title: "Módulo 2: Planificación y Operaciones",
  content: "Técnicas de inventario forestal, planificación de cosechas, manejo de suelos y gestión de recursos naturales utilizando herramientas tecnológicas de precisión.",
  icon: Zap
}, {
  id: "mod-3",
  title: "Módulo 3: Seguridad y Liderazgo de Equipos",
  content: "Protocolos de seguridad en entornos forestales, prevención de riesgos laborales, coordinación de cuadrillas de trabajo y logística operativa eficiente en terreno.",
  icon: Shield
}, {
  id: "mod-4",
  title: "Módulo 4: Normativas y Certificaciones",
  content: "Legislación ambiental vigente, procesos de certificación forestal internacional (FSC/PEFC) y estándares de calidad para la industria maderera y de exportación.",
  icon: Award
}];
const requirements = ["No se requieren conocimientos técnicos previos.", "Conexión a internet estable (mínimo 10 Mbps).", "Computadora portátil o de escritorio (Windows o Mac).", "Disponibilidad de 4 a 6 horas semanales para estudio.", "Ganas de aprender y compromiso con el desarrollo profesional."];
export default function DetallesDelCurso() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: statsRef,
    isVisible: areStatsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="detalles-del-curso" className="bg-[hsl(222_47%_11%)] py-[120px] relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-primary text-4xl md:text-5xl font-bold mb-6 tracking-tight">
            Detalles del Programa
          </h2>
          <p className="text-slate-300 text-lg">
            Todo lo que necesitas saber sobre nuestro curso estructurado para llevarte desde los conceptos básicos hasta el nivel profesional.
          </p>
        </div>

        {}
        <div ref={statsRef} className={`grid grid-cols-1 md:grid-cols-3 gap-6 mb-24 transition-all duration-1000 delay-200 transform ${areStatsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {stats.map((stat, i) => <Card key={stat.id} data-index={i} className="bg-[hsl(222_47%_15%/0.7)] backdrop-blur-[12px] border-white/10 transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(var(--color-primary),0.15)] border-0">
              <CardContent className="p-8 flex flex-col items-center text-center h-full justify-center gap-4">
                <div className="p-4 rounded-full bg-white/5 text-primary mb-2">
                  <stat.icon className="size-8" />
                </div>
                <h3 className="text-slate-400 font-medium uppercase tracking-wider text-sm">
                  {stat.label}
                </h3>
                <div className="flex items-baseline gap-1">
                  <span className={`font-mono text-4xl md:text-5xl font-bold tracking-tighter ${stat.valueColor}`}>
                    {stat.value}
                  </span>
                  {stat.suffix && <span className="text-slate-400 font-medium">{stat.suffix}</span>}
                </div>
              </CardContent>
            </Card>)}
        </div>

        {}
        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 transition-all duration-1000 delay-400 transform ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div>
            <h3 className="text-primary text-3xl font-bold mb-8 flex items-center gap-3">
              <BookOpen className="size-8 text-secondary" />
              Plan de Estudios
            </h3>
            <Accordion type="single" collapsible className="w-full space-y-4">
              {modules.map((module, i) => <AccordionItem key={module.id} value={module.id} data-index={i} className="bg-[hsl(222_47%_15%/0.4)] border border-white/5 rounded-xl px-6 overflow-hidden data-[state=open]:bg-[hsl(222_47%_15%/0.8)] transition-colors">
                  <AccordionTrigger className="text-white hover:text-primary hover:no-underline py-6">
                    <div className="flex items-center gap-4 text-left">
                      <div className="p-2 rounded-lg bg-secondary/10 text-secondary">
                        <module.icon className="size-5" />
                      </div>
                      <span className="font-semibold text-lg">{module.title}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300 leading-relaxed pb-6 pt-2 pl-16">
                    {module.content}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {}
          <div>
            <h3 className="text-primary text-3xl font-bold mb-8 flex items-center gap-3">
              <CheckCircle2 className="size-8 text-secondary" />
              Requisitos Previos
            </h3>
            <Card className="bg-[hsl(222_47%_15%/0.4)] border-white/5 border-0">
              <CardContent className="p-8">
                <ul className="space-y-6">
                  {requirements.map((req, i) => <li key={i} data-index={i} className="flex items-start gap-4">
                      <div className="mt-1 p-1 rounded-full bg-secondary/20 text-secondary shrink-0">
                        <CheckCircle2 className="size-4" />
                      </div>
                      <span className="text-slate-300 text-lg leading-relaxed">{req}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}